import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Activity, AlertTriangle, ShieldAlert, WifiOff, XCircle, Chrome, Gauge, CheckCircle2, MailX } from "lucide-react";

/**
 * Iter-45b — Scanner Health card (Owner Dashboard, admin-only).
 *
 * Lightweight operational health summary that answers "is the monitoring
 * system itself healthy?" from signals we already store. Not customer-
 * facing; not elaborate. Six metrics, each a 24h rolling count so a
 * spike stands out immediately.
 *
 * Thresholds are honest — green when the number matches steady state,
 * amber on any elevated activity worth glancing at, red when something
 * needs owner attention. Volume denominators (total / deep scans) are
 * shown as raw counts so the ratios above are interpretable.
 */
function pillFor(count, { amberAt, redAt }) {
  if (count >= redAt)   return "bg-red-50 text-red-700 border-red-100";
  if (count >= amberAt) return "bg-amber-50 text-amber-700 border-amber-100";
  return "bg-emerald-50 text-emerald-700 border-emerald-100";
}

function Metric({ icon: Icon, label, value, sub, pill }) {
  return (
    <div className="flex-1 min-w-[160px] px-4 py-3 border-r border-black/5 last:border-r-0" data-testid={`scanner-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-charcoal-400" />
        <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-charcoal-400">{label}</p>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <p className="text-2xl font-semibold tracking-tight text-charcoal-900 tabular-nums">{value}</p>
        {pill && <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9.5px] font-mono uppercase tracking-widest border ${pill.cls}`}>{pill.label}</span>}
      </div>
      {sub && <p className="text-[10.5px] text-charcoal-500 font-mono mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ScannerHealthCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const r = await api.get("/admin/scanner-health");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setErr("Could not load scanner health.");
      }
    }
    load();
    // Auto-refresh every 60s so the card stays live while the owner
    // watches launch day traffic without needing to reload the page.
    const id = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const rg    = data?.regression_guard_events_24h ?? 0;
  const fw    = data?.firewall_interference_24h   ?? 0;
  const un    = data?.unreachable_reports_24h     ?? 0;
  const fj    = data?.failed_scan_jobs_24h        ?? 0;
  const t2    = data?.tier2_host_escalations      ?? 0;
  const sup   = data?.alerts_suppressed_24h       ?? 0;
  const total = data?.total_scans_24h             ?? 0;
  const deep  = data?.deep_scans_24h              ?? 0;

  // Overall pill — worst of the actionable signals.
  // `alerts_suppressed` is a SAFETY-NET signal: a small trickle is healthy
  // (the Iter-47 gate doing its job). A spike (>=25/24h) means scans are
  // oscillating red/green and warrants investigation.
  const anyRed   = fj >= 3 || rg >= 5 || un >= 20 || sup >= 25;
  const anyAmber = fj >= 1 || rg >= 1 || un >= 5 || fw >= 20 || sup >= 10;
  const overallPill = anyRed
    ? { label: "attention", cls: "bg-red-50 text-red-700 border-red-100", Icon: XCircle }
    : anyAmber
    ? { label: "watch",     cls: "bg-amber-50 text-amber-700 border-amber-100", Icon: AlertTriangle }
    : { label: "healthy",   cls: "bg-emerald-50 text-emerald-700 border-emerald-100", Icon: CheckCircle2 };

  return (
    <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden" data-testid="scanner-health-card">
      <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-charcoal-500" />
          <h3 className="text-sm font-medium text-charcoal-900">Scanner health</h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-charcoal-400">last 24h · owner only</span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border ${overallPill.cls}`} data-testid="scanner-health-overall">
          <overallPill.Icon className="w-3 h-3" /> {overallPill.label}
        </span>
      </div>

      {err ? (
        <p className="px-5 py-4 text-xs text-red-600">{err}</p>
      ) : !data ? (
        <p className="px-5 py-4 text-xs text-charcoal-400">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap divide-y sm:divide-y-0 sm:divide-x divide-black/5">
            <Metric icon={ShieldAlert} label="Regression guard" value={rg}
                    sub="Reports flagged by Iter-45 guard"
                    pill={rg > 0 ? { label: rg >= 5 ? "high" : "watch",
                                     cls: pillFor(rg, { amberAt: 1, redAt: 5 }) } : null} />
            <Metric icon={WifiOff} label="Unreachable" value={un}
                    sub={total > 0 ? `${((un / Math.max(total, 1)) * 100).toFixed(1)}% of scans` : "no scans yet"}
                    pill={un > 0 ? { label: un >= 20 ? "high" : "watch",
                                     cls: pillFor(un, { amberAt: 5, redAt: 20 }) } : null} />
            <Metric icon={AlertTriangle} label="Firewall interference" value={fw}
                    sub="Reports w/ monitoring_blocked"
                    pill={fw >= 20 ? { label: "watch", cls: pillFor(fw, { amberAt: 20, redAt: 100 }) } : null} />
            <Metric icon={XCircle} label="Failed scan jobs" value={fj}
                    sub="Hard-timeout / crash"
                    pill={fj > 0 ? { label: fj >= 3 ? "high" : "watch",
                                     cls: pillFor(fj, { amberAt: 1, redAt: 3 }) } : null} />
            <Metric icon={MailX} label="Alerts suppressed" value={sup}
                    sub="Iter-47 freshness gate — safety net"
                    pill={sup > 0 ? { label: sup >= 25 ? "high" : "watch",
                                       cls: pillFor(sup, { amberAt: 10, redAt: 25 }) } : null} />
          </div>
          <div className="flex flex-wrap divide-y sm:divide-y-0 sm:divide-x divide-black/5 border-t border-black/5 bg-charcoal-50/40">
            <Metric icon={Chrome} label="Tier-2 hosts" value={t2}
                    sub="Customers behind aggressive WAFs (all-time)" />
            <Metric icon={Gauge} label="Total scans" value={total}
                    sub={`${deep} deep · ${Math.max(total - deep, 0)} basic`} />
          </div>
        </>
      )}
    </section>
  );
}
